/**
 * Feature Flags & Release Management
 * Controla el lanzamiento programado de nuevas funcionalidades
 */

// Fecha de lanzamiento oficial: 30 de Mayo 2026
export const RELEASE_DATE = new Date('2026-05-30T00:00:00');

// Versión actual
export const APP_VERSION = '2.0.0';

// Features con fecha de lanzamiento
export const FEATURES = {
  RF_NETWORK_CONTROL: {
    id: 'rf_network_control',
    name: 'Control RF en Red',
    description: 'Envía frecuencias directamente a receptores Shure y Sennheiser conectados a tu red',
    icon: 'Wifi',
    releaseDate: RELEASE_DATE,
    category: 'hardware'
  },
  LOCAL_AGENT: {
    id: 'local_agent',
    name: 'Agente Local',
    description: 'Detecta y controla equipos RF en tu red local con un simple instalador',
    icon: 'Server',
    releaseDate: RELEASE_DATE,
    category: 'hardware'
  },
  TELEGRAM_ALERTS: {
    id: 'telegram_alerts',
    name: 'Alertas Telegram',
    description: 'Recibe notificaciones instantáneas de eventos RF críticos en tu Telegram',
    icon: 'Send',
    releaseDate: RELEASE_DATE,
    category: 'notifications'
  }
};

/**
 * Verifica si una feature está disponible (ya pasó su fecha de lanzamiento o usuario tiene early access)
 * @param {string} featureId - ID de la feature
 * @param {object} user - Objeto de usuario (opcional, si tiene early_access)
 */
export const isFeatureAvailable = (featureId, user = null) => {
  // Si el usuario tiene early access, siempre disponible
  if (user?.early_access) {
    return true;
  }
  
  // Check localStorage for user with early_access (for when user prop is not passed)
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      if (parsedUser?.early_access) {
        return true;
      }
    }
  } catch (e) {
    // Ignore parse errors
  }
  
  const feature = Object.values(FEATURES).find(f => f.id === featureId);
  if (!feature) return true; // Features no listadas están disponibles
  
  const now = new Date();
  return now >= feature.releaseDate;
};

/**
 * Verifica si todas las features v2.0 están disponibles
 * @param {object} user - Objeto de usuario (opcional)
 */
export const isV2Released = (user = null) => {
  // Si el usuario tiene early access, siempre disponible
  if (user?.early_access) {
    return true;
  }
  
  // Check localStorage
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      if (parsedUser?.early_access) {
        return true;
      }
    }
  } catch (e) {
    // Ignore parse errors
  }
  
  const now = new Date();
  return now >= RELEASE_DATE;
};

/**
 * Obtiene el tiempo restante hasta el lanzamiento
 */
export const getTimeUntilRelease = () => {
  const now = new Date();
  const diff = RELEASE_DATE - now;
  
  if (diff <= 0) {
    return { released: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
  }
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  
  return { released: false, days, hours, minutes, seconds };
};

/**
 * Formatea la fecha de lanzamiento para mostrar
 */
export const formatReleaseDate = () => {
  return RELEASE_DATE.toLocaleDateString('es-CL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

/**
 * Lista de novedades para el modal
 */
export const RELEASE_NOTES = [
  {
    version: '2.0.0',
    date: RELEASE_DATE,
    title: 'Control RF en Red + Agente Local',
    highlights: [
      {
        icon: 'Wifi',
        title: 'Control RF en Red',
        description: 'Envía frecuencias calculadas directamente a tus receptores Shure y Sennheiser via IP',
        color: '#007AFF'
      },
      {
        icon: 'Server',
        title: 'Agente Local',
        description: 'Instala un pequeño agente en tu PC para detectar automáticamente equipos RF en tu red',
        color: '#34C759'
      },
      {
        icon: 'Send',
        title: 'Alertas Telegram',
        description: 'Recibe alertas instantáneas cuando la señal RF cae, batería baja o dispositivo offline',
        color: '#0088CC'
      },
      {
        icon: 'Download',
        title: 'Instaladores Fáciles',
        description: 'Descarga instaladores para Windows y Mac con un clic - sin usar terminal',
        color: '#AF52DE'
      }
    ]
  }
];

/**
 * Verifica si el usuario ya vio el modal de novedades para esta versión
 */
export const hasSeenReleaseNotes = (version = APP_VERSION) => {
  const seen = localStorage.getItem(`release_notes_seen_${version}`);
  return seen === 'true';
};

/**
 * Marca que el usuario vio el modal de novedades
 */
export const markReleaseNotesSeen = (version = APP_VERSION) => {
  localStorage.setItem(`release_notes_seen_${version}`, 'true');
};

/**
 * Verifica si debe mostrar el badge "NUEVO" para una feature
 */
export const shouldShowNewBadge = (featureId) => {
  if (!isFeatureAvailable(featureId)) return false;
  
  // Mostrar badge por 14 días después del lanzamiento
  const feature = Object.values(FEATURES).find(f => f.id === featureId);
  if (!feature) return false;
  
  const now = new Date();
  const daysSinceRelease = (now - feature.releaseDate) / (1000 * 60 * 60 * 24);
  
  // También verificar si el usuario ya visitó la sección
  const visited = localStorage.getItem(`feature_visited_${featureId}`);
  
  return daysSinceRelease <= 14 && !visited;
};

/**
 * Marca que el usuario visitó una feature
 */
export const markFeatureVisited = (featureId) => {
  localStorage.setItem(`feature_visited_${featureId}`, 'true');
};
